/**
 * API Service pour le Rachat Intelligent (UE-centrique, basé sur les notes d'examen)
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithHeaders } from './baseApi';

// ---------------------------------------------------------------------------
// Interfaces — Paramètres
// ---------------------------------------------------------------------------

export interface ParametresRachat {
  seuil_admission: string;
  seuil_eliminatoire_ue: string;
  seuil_minimum_rachat: string;
  plafond_augmentation: string;
  note_eliminatoire: string;
}

// ---------------------------------------------------------------------------
// Interfaces — Analyse (UE-centrique)
// ---------------------------------------------------------------------------

export interface AnalyseECUE {
  matiere_id: number;
  code_matiere: string;
  nom_matiere: string;
  moyenne_ecue: number | null;
  note_examen_actuelle: number | null;
  note_devoir: number | null;
  coef_examen: number;
  coef_devoir: number;
  credits: number;
  absent: boolean;
  est_rattrapage: boolean;
  modifiable: boolean;
}

export interface AnalyseUE {
  code_ue: string;
  nom_ue: string;
  moyenne_ue: number | null;
  credits: number;
  credits_obtenus: number;
  validee: boolean;
  ecues: AnalyseECUE[];
}

export interface EtudiantRachat {
  id: number;
  matricule: string;
  nom: string;
  prenoms: string;
  niveau: string;
  classe: string;
  option: string;
  moy_semestre_actuelle: number;
  deficit: number;
  credits_obtenus: number;
  credits_requis: number;
  ues: AnalyseUE[];
  eligible: boolean;
  raisons: string[];
  rattrapage_saisie: boolean;
}

export interface AnalyseRachatResponse {
  etudiants: EtudiantRachat[];
  parametres: ParametresRachat;
  total: number;
  eligibles: number;
  message?: string;
}

interface AnalyseRachatPayload {
  niveau?: string;
  semestre?: number;
  classe?: string;
  option?: string;
}

// ---------------------------------------------------------------------------
// Interfaces — Proposer rachats
// ---------------------------------------------------------------------------

interface ProposerRachatsPayload {
  etudiants: number[];
  semestre?: number;
  niveau?: string;
}

// ---------------------------------------------------------------------------
// Interfaces — Simulation
// ---------------------------------------------------------------------------

export interface AjustementNote {
  matiere_id: number;
  etudiant_id: number;
  note_proposee: number;
}

export interface SimulationPayload {
  ajustements: AjustementNote[];
}

export interface SimulationECUE {
  matiere_id: number;
  code_matiere: string;
  nom_matiere: string;
  note_examen_avant: number | null;
  note_examen_apres: number | null;
  moyenne_ecue_avant: number | null;
  moyenne_ecue_apres: number | null;
}

export interface SimulationUE {
  code_ue: string;
  nom_ue: string;
  moyenne_avant: number;
  moyenne_apres: number;
  validee_avant: boolean;
  validee_apres: boolean;
  ecues?: SimulationECUE[];
}

export interface SimulationEtudiant {
  etudiant_id: number;
  etudiant_matricule: string;
  etudiant_nom: string;
  moy_semestre_avant: number;
  moy_semestre_apres: number;
  semestre_valide: boolean;
  ues: SimulationUE[];
}

export interface SimulationResponse {
  resultats: SimulationEtudiant[];
}

// ---------------------------------------------------------------------------
// Interfaces — Demandes & Historique
// ---------------------------------------------------------------------------

export interface DemandeRachat {
  id: number;
  etudiant: number;
  etudiant_matricule: string;
  etudiant_nom: string;
  etudiant_classe: string;
  etudiant_niveau: string;
  etudiant_option: string;
  matiere: number;
  matiere_nom: string;
  matiere_code: string;
  ue_code: string | null;
  ue_nom: string | null;
  note_originale: string;
  note_proposee: string;
  justification: string;
  statut: 'en_attente' | 'approuve' | 'rejete' | 'annule';
  date_demande: string;
  date_traitement: string | null;
  traite_par: number | null;
  traite_par_username: string | null;
}

export interface RachatApplique {
  id: number;
  etudiant: number;
  etudiant_matricule: string;
  etudiant_nom: string;
  matiere: number;
  matiere_nom: string;
  matiere_code: string;
  ue_code: string | null;
  ue_nom: string | null;
  demande: number | null;
  type_operation: 'rachat' | 'annulation';
  note_avant: string;
  note_apres: string;
  justification: string;
  applique_par: number | null;
  applique_par_username: string | null;
  date_application: string;
}

// ─── Vue commission/jury : rachats par classe ─────────────────────────────────

export interface RachatNoteCommission {
  matiere_id: number;
  matiere_code: string;
  matiere_nom: string;
  ue_code: string | null;
  ue_nom: string | null;
  note_avant: number;
  note_apres: number;
  note_examen_actuelle: number | null;
  date_application: string;
  applique_par: string | null;
  justification: string;
  rachat_actif: boolean;
}

export interface EtudiantRachatCommission {
  matricule: string;
  nom: string;
  prenoms: string;
  nb_notes_rachetees: number;
  rachats: RachatNoteCommission[];
}

export interface RachatsParClasseResponse {
  classe_code: string;
  semestre: number;
  annee_academique: string;
  inclure_annules: boolean;
  total_etudiants_avec_rachat: number;
  total_notes_rachetees: number;
  etudiants: EtudiantRachatCommission[];
}

export interface RachatsParClasseParams {
  classe_code: string;
  semestre: number;
  annee_academique: string;
  inclure_annules?: boolean;
}

export interface DemandesRachatFilters {
  statut?: string;
  etudiant?: number;
  niveau?: string;
  classe?: string;
  option?: string;
  search?: string;
}

interface BatchPayload {
  demande_ids: number[];
}

interface BatchResponse {
  approuvees?: number;
  rejetees?: number;
  total_demandees: number;
}

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

// ── Types Rachat Batch ─────────────────────────────────────────────
export interface ProposerBatchPayload {
  annee_academique: string;
  semestre: number;
  pole?: string;
  niveau?: string;
  option?: string;
  classe?: string;
  moy_min?: number | null;
  moy_max?: number | null;
  cas_filtre?: 'tous' | 'sous_admission' | 'eliminatoire' | 'mixte';
  plafond_augmentation?: number;
  /** Session ordinaire en dernier recours, une fois les rattrapages épuisés (off par défaut). */
  autoriser_so?: boolean;
  /** Inclure les SO à 0 (off : un 0 vaut non-participation). Sans effet sans autoriser_so. */
  autoriser_so_zero?: boolean;
  /** Application par paquets : réutiliser le lot du premier paquet. */
  batch_id?: string | null;
  /** Propager les changements aux notes de devoir si SR/SO == D1 == D2 (rare). Off par défaut. */
  propager_aux_devoirs?: boolean;
  /** Limiter l'application à ces etudiants (sélection manuelle dans la preview). */
  etudiant_ids?: number[];
  dry_run: boolean;
}

export interface PropositionEtudiantBatch {
  etudiant_id: number;
  matricule: string;
  nom: string;
  classe: string;
  option: string;
  cas: CasRachat;
  moy_avant: number;
  /** Moyenne après rachat, donnée par le calcul officiel (transaction annulée). */
  moy_apres_simule: number;
  moy_apres_plan?: number | null;
  /** Le plan touche la session ordinaire (dernier recours autorisé). */
  utilise_so?: boolean;
  credits_apres?: number | null;
  credits_requis?: number | null;
  ues_elim_avant?: string[];
  /** Moyenne officielle avant toute écriture (fix 2026-09-17) */
  moy_avant_officielle?: number | null;
  /** Notes à 0 que l'application créerait pour permettre le calcul */
  nb_notes_completees?: number;
  ponderation_credits?: boolean;
  gain: number;
  nb_ues_total: number;
  nb_ues_validees_avant: number;
  nb_ues_validees_apres: number;
  nb_ues_non_validees_avant: number;
  nb_ues_non_validees_apres: number;
  ues_traitees: Array<{
    code_ue: string;
    nom_ue: string;
    moy_avant: number;
    moy_apres: number;
    etait_eliminatoire: boolean;
    devient_validee: boolean;
  }>;
  notes_modifiees: Array<{
    matiere_id: number;
    matiere_code: string;
    matiere_nom: string;
    ue_code?: string;
    /** Note de session ordinaire (pour une SR relevée au-dessus d'elle) */
    so_val?: number | null;
    source: 'SO' | 'SR';
    note_avant: number;
    note_apres: number;
    augmentation: number;
    devoir_conjoint: boolean;
  }>;
}

export interface ProposerBatchResponse {
  dry_run: boolean;
  batch_id: string | null;
  scope: any;
  total_scope: number;
  total_eligibles: number;
  nb_etudiants_traites: number;
  nb_notes_examen_modifiees: number;
  nb_notes_devoir_modifiees: number;
  /** Étudiants réellement rachetés, avec la moyenne recalculée (fix 2026-09-17) */
  appliques?: Array<{
    etudiant_id: number; matricule: string; nom: string; classe: string; cas: string;
    moy_avant: number | null; moy_apres_simule: number | null; moy_apres: number | null; valide: boolean;
  }>;
  /** Étudiants dont le rachat a été annulé, cible non atteinte ou données modifiées */
  echecs?: Array<{ etudiant_id: number; matricule: string; nom: string; classe: string; motif: string }>;
  /** Moyennes stockées périmées : la simulation part des moyennes recalculées */
  nb_moyennes_rafraichies?: number;
  moyennes_rafraichies?: Array<{
    etudiant_id: number; matricule: string; classe: string; moy_stockee: number | null; moy_reelle: number | null;
  }>;
  propositions: PropositionEtudiantBatch[];
  autoriser_so?: boolean;
  /** Cas détectés que les règles ne permettent pas de racheter, avec le motif. */
  non_rachetables?: NonRachetable[];
}

export type CasRachat = 'sous_admission' | 'eliminatoire' | 'mixte' | 'manuel';

export interface NonRachetable {
  etudiant_id: number;
  matricule: string;
  nom: string;
  classe: string;
  cas: CasRachat;
  moy_avant: number | null;
  moy_max_atteignable: number | null;
  ues_elim_restantes: string[];
  /** Rachetable si l'on autorise la session ordinaire en dernier recours */
  possible_avec_so: boolean;
  motif: string;
}

// ── Rachat manuel (03/10/2026) ──
export interface ManuelContexte {
  etudiant_id: number;
  semestre: number;
  annee_academique: string;
  plafond_augmentation: number;
  autoriser_so?: boolean;
  autoriser_so_zero?: boolean;
  propager_aux_devoirs?: boolean;
}

export interface ManuelNote { matiere_id: number; source: 'SO' | 'SR' | 'D1' | 'D2'; note: number }

export interface ManuelEcue {
  matiere_id: number;
  matiere_code: string;
  matiere_nom: string;
  moyenne_ecue: number | null;
  moy_devoirs: number | null;
  so: number | null;
  sr: number | null;
  verrouille: boolean;
  source_relevable: 'SO' | 'SR' | null;
  note_actuelle: number | null;
  note_max: number | null;
  /** Devoirs (rachat manuel, force majeure) */
  d1: number | null;
  d2: number | null;
  absent1: boolean;
  absent2: boolean;
  devoir_editable: boolean;
  devoir_verrouille: boolean;
  d1_max: number | null;
  d2_max: number | null;
}

export interface ManuelDossier {
  etudiant_id: number;
  matricule: string;
  nom: string;
  classe: string;
  semestre: number;
  annee_academique: string;
  moy_sem: number;
  valide: boolean;
  seuil_admission: number;
  seuil_eliminatoire: number;
  plafond: number;
  cas: CasRachat | null;
  ues: Array<{
    ue_id: number; code_ue: string; nom_ue: string; moyenne_ue: number | null;
    validee: boolean; eliminatoire: boolean; ecues: ManuelEcue[];
  }>;
  proposition_auto: { moy_apres: number; utilise_so: boolean; notes: ManuelNote[] } | null;
  diagnostic_auto?: { motif: string; possible_avec_so: boolean; moy_max_atteignable: number | null };
}

export interface ManuelSimulation {
  moy_avant: number;
  moy_apres: number | null;
  valide: boolean;
  credits_obtenus: number | null;
  credits_requis: number | null;
  ues: Array<{ ue_id: number; moyenne_ue: number | null; validee: boolean; eliminatoire: boolean }>;
  nb_notes: number;
  devoirs_conjoints: number;
}

export interface ManuelApplication {
  batch_id: string;
  moy_avant: number | null;
  moy_apres: number | null;
  nb_notes_examen: number;
  nb_notes_devoir: number;
}

export interface AnnulerBatchResponse {
  batch_id: string;
  nb_notes_restaurees: number;
  nb_etudiants_recalcules: number;
  /** Notes introuvables sous leur semestre : laissées telles quelles, à vérifier */
  non_restaurees?: Array<{ matricule: string; matiere: string; source: string; note_avant: number | null; note_apres: number | null }>;
}

export interface HistoriqueBatchesResponse {
  total: number;
  batches: Array<{
    batch_id: string;
    nb_modifications: number;
    nb_etudiants: number;
    date_debut: string | null;
    date_fin: string | null;
    statut: 'rachat' | 'annule';
    cas: string;
  }>;
}

export interface BatchDetailsResponse {
  batch_id: string;
  total_etudiants: number;
  total_modifications: number;
  etudiants: Array<{
    etudiant_id: number;
    matricule: string;
    nom: string;
    classe: string;
    option: string;
    cas: string;
    modifications: Array<{
      matiere_id: number;
      matiere_code: string;
      matiere_nom: string;
      source: 'SO' | 'SR' | 'D1' | 'D2';
      note_avant: number | null;
      note_apres: number | null;
      type_operation: 'rachat' | 'annulation';
      date_application: string;
    }>;
  }>;
}

// ── Types Rapport Rachats ──────────────────────────────────────────
export interface RapportRachatsResponse {
  rachats: Array<{
    batch_id: string;
    date_application: string | null;
    etudiant_id: number;
    matricule: string;
    nom: string;
    classe: string;
    niveau: string;
    option: string;
    pole: string;
    cas: string;
    moy_sem_avant: number | null;
    moy_sem_apres: number | null;
    valide_avant: boolean | null;
    valide_apres: boolean | null;
    nb_ues_validees_avant: number | null;
    nb_ues_validees_apres: number | null;
    nb_ues_total: number | null;
    is_annule: boolean;
    notes_modifiees: Array<{
      matiere_code: string;
      matiere_nom: string;
      source: string;
      note_avant: number | null;
      note_apres: number | null;
      gain: number;
    }>;
  }>;
  meta: {
    total_etudiants: number;
    admis_grace_au_rachat: number;
    admis_avant_rachat: number;
    gain_moyen: number;
    gain_max: number;
    gain_min: number;
    nb_sous_admission: number;
    nb_eliminatoire: number;
    periode: { date_min: string | null; date_max: string | null };
  };
  par_pole: Array<{ libelle: string; nb_etu: number; nb_admis_grace_rachat: number; gain_moyen: number }>;
  par_niveau: Array<{ libelle: string; nb_etu: number; nb_admis_grace_rachat: number; gain_moyen: number }>;
  par_option: Array<{ libelle: string; nb_etu: number; nb_admis_grace_rachat: number; gain_moyen: number }>;
  par_classe: Array<{ libelle: string; nb_etu: number; nb_admis_grace_rachat: number; gain_moyen: number }>;
  par_tranche_avant: Array<{ libelle: string; nb_etu: number; nb_admis_grace_rachat: number }>;
}

export interface RapportRachatsParams {
  date_min?: string;
  date_max?: string;
  niveau?: string;
  pole?: string;
  option?: string;
  classe?: string;
  cas?: 'sous_admission' | 'eliminatoire';
  include_annules?: boolean;
  batch_id?: string;
}

export const rachatApi = createApi({
  reducerPath: 'rachatApi',
  baseQuery: baseQueryWithHeaders,
  tagTypes: ['DemandeRachat', 'RachatApplique', 'ParametresRachat'],
  endpoints: (builder) => ({
    getParametresRachat: builder.query<ParametresRachat, void>({
      query: () => '/rachat-intelligent/parametres/',
      providesTags: ['ParametresRachat'],
    }),

    analyserRachat: builder.mutation<AnalyseRachatResponse, AnalyseRachatPayload>({
      query: (payload) => ({
        url: '/rachat-intelligent/analyser/',
        method: 'POST',
        body: payload,
      }),
    }),

    proposerRachats: builder.mutation<any, ProposerRachatsPayload>({
      query: (payload) => ({
        url: '/rachat-intelligent/proposer_rachats/',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['DemandeRachat'],
    }),

    simulerRachat: builder.mutation<SimulationResponse, SimulationPayload>({
      query: (payload) => ({
        url: '/rachat-intelligent/simuler/',
        method: 'POST',
        body: payload,
      }),
    }),

    getDemandesRachat: builder.query<DemandeRachat[], DemandesRachatFilters>({
      query: (params) => ({
        url: '/demandes-rachat/',
        params: { ...params, page_size: 1000 },
      }),
      transformResponse: (response: any) =>
        Array.isArray(response) ? response : (response?.results ?? []),
      providesTags: ['DemandeRachat'],
    }),

    approuverDemande: builder.mutation<DemandeRachat, number>({
      query: (id) => ({
        url: `/demandes-rachat/${id}/approuver/`,
        method: 'POST',
      }),
      invalidatesTags: ['DemandeRachat', 'RachatApplique'],
    }),

    rejeterDemande: builder.mutation<DemandeRachat, number>({
      query: (id) => ({
        url: `/demandes-rachat/${id}/rejeter/`,
        method: 'POST',
      }),
      invalidatesTags: ['DemandeRachat'],
    }),

    approuverLot: builder.mutation<BatchResponse, BatchPayload>({
      query: (payload) => ({
        url: '/demandes-rachat/approuver_lot/',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['DemandeRachat', 'RachatApplique'],
    }),

    rejeterLot: builder.mutation<BatchResponse, BatchPayload>({
      query: (payload) => ({
        url: '/demandes-rachat/rejeter_lot/',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['DemandeRachat'],
    }),

    getRachatsAppliques: builder.query<RachatApplique[], { etudiant?: number; type_operation?: string }>({
      query: (params) => ({
        url: '/rachats-appliques/',
        params: { ...params, page_size: 1000 },
      }),
      transformResponse: (response: any) =>
        Array.isArray(response) ? response : (response?.results ?? []),
      providesTags: ['RachatApplique'],
    }),

    annulerRachat: builder.mutation<any, number>({
      query: (id) => ({
        url: `/rachats-appliques/${id}/annuler/`,
        method: 'POST',
      }),
      invalidatesTags: ['DemandeRachat', 'RachatApplique'],
    }),

    // ── Rachat batch (scope pôle/niveau/option/classe + tranche moy) ──
    proposerBatchRachat: builder.mutation<ProposerBatchResponse, ProposerBatchPayload>({
      query: (payload) => ({
        url: '/rachat-intelligent/proposer-batch/',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_r, _e, arg) => arg.dry_run
        ? []
        : ['DemandeRachat', 'RachatApplique'],
    }),
    manuelDossier: builder.mutation<ManuelDossier, ManuelContexte>({
      query: (body) => ({ url: '/rachat-intelligent/manuel-dossier/', method: 'POST', body }),
    }),
    manuelSimuler: builder.mutation<ManuelSimulation, ManuelContexte & { notes: ManuelNote[] }>({
      query: (body) => ({ url: '/rachat-intelligent/manuel-simuler/', method: 'POST', body }),
    }),
    manuelAppliquer: builder.mutation<ManuelApplication, ManuelContexte & { notes: ManuelNote[]; commentaire?: string }>({
      query: (body) => ({ url: '/rachat-intelligent/manuel-appliquer/', method: 'POST', body }),
      invalidatesTags: ['DemandeRachat', 'RachatApplique'],
    }),
    annulerBatchRachat: builder.mutation<AnnulerBatchResponse, { batch_id: string }>({
      query: (payload) => ({
        url: '/rachat-intelligent/annuler-batch/',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['DemandeRachat', 'RachatApplique'],
    }),
    getHistoriqueBatches: builder.query<HistoriqueBatchesResponse, void>({
      query: () => '/rachat-intelligent/historique-batches/',
      providesTags: ['RachatApplique'],
    }),
    getBatchDetails: builder.query<BatchDetailsResponse, { batch_id: string }>({
      query: ({ batch_id }) => `/rachat-intelligent/batch-details/?batch_id=${batch_id}`,
      providesTags: ['RachatApplique'],
    }),
    getRapportRachats: builder.query<RapportRachatsResponse, RapportRachatsParams | void>({
      query: (params) => {
        const qs = new URLSearchParams();
        if (params) {
          Object.entries(params).forEach(([k, v]) => {
            if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
          });
        }
        return `/rachat-intelligent/rapport-rachats/?${qs.toString()}`;
      },
      providesTags: ['RachatApplique'],
    }),

    /** Vue commission : rachats appliqués groupés par étudiant pour une classe + semestre. */
    getRachatsParClasse: builder.query<RachatsParClasseResponse, RachatsParClasseParams>({
      query: (params) => {
        const qs = new URLSearchParams({
          classe_code: params.classe_code,
          semestre: String(params.semestre),
          annee_academique: params.annee_academique,
        });
        if (params.inclure_annules) qs.set('inclure_annules', 'true');
        return `/rachats-appliques/par-classe/?${qs.toString()}`;
      },
      providesTags: ['RachatApplique'],
    }),
  }),
});

export const {
  useGetParametresRachatQuery,
  useAnalyserRachatMutation,
  useProposerRachatsMutation,
  useSimulerRachatMutation,
  useGetDemandesRachatQuery,
  useApprouverDemandeMutation,
  useRejeterDemandeMutation,
  useApprouverLotMutation,
  useRejeterLotMutation,
  useGetRachatsAppliquesQuery,
  useAnnulerRachatMutation,
  useGetRachatsParClasseQuery,
  useLazyGetRachatsParClasseQuery,
  useProposerBatchRachatMutation,
  useAnnulerBatchRachatMutation,
  useManuelDossierMutation,
  useManuelSimulerMutation,
  useManuelAppliquerMutation,
  useGetHistoriqueBatchesQuery,
  useGetBatchDetailsQuery,
  useLazyGetBatchDetailsQuery,
  useGetRapportRachatsQuery,
} = rachatApi;
